// Export-Dialog fuer die integrierte Planung: Zeitraum (vordefiniert oder
// frei), Format (Excel/PDF) und Layout (Standard / Banken PLAN-IST-nebeneinander).
// Laedt fuer Mehrjahres-/Vergleichs-Zeitraeume den aktiven Plan je Jahr
// (wird angelegt, falls nicht vorhanden) und exportiert ihn.

import { useMemo, useState } from 'react'
import { Download, FileSpreadsheet, FileText } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Input,
  Modal,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { exportPlans, type ExportFormat, type ExportLayout } from './plan-export.ts'
import type { PlanComparisonResponse } from './types.ts'

type Preset =
  | 'current' | 'last' | 'next' | 'compare' | 'custom'
  | 'plan1' | 'plan2' | 'plan3'

const PRESET_LABELS: Record<Preset, string> = {
  current: 'Aktuelles Jahr',
  last: 'Letztes Jahr',
  next: 'Kommendes Jahr',
  compare: 'Vergleich aktuelles / letztes Jahr',
  custom: 'Zeitraum auswählen',
  plan1: 'Planung 1 Jahr',
  plan2: 'Planung 2 Jahre',
  plan3: 'Planung 3 Jahre',
}
const PRESET_ORDER: Preset[] = ['current', 'last', 'next', 'compare', 'custom', 'plan1', 'plan2', 'plan3']

/** Liefert die Liste der zu exportierenden Jahre fuer ein Preset (Anker = anchorYear). */
function resolveYears(preset: Preset, anchorYear: number, from: number, to: number): number[] {
  switch (preset) {
    case 'current': return [anchorYear]
    case 'last': return [anchorYear - 1]
    case 'next': return [anchorYear + 1]
    case 'compare': return [anchorYear - 1, anchorYear]
    case 'custom': {
      const lo = Math.min(from, to)
      const hi = Math.max(from, to)
      const out: number[] = []
      for (let y = lo; y <= hi && out.length < 10; y++) out.push(y)
      return out
    }
    case 'plan1': return [anchorYear]
    case 'plan2': return [anchorYear, anchorYear + 1]
    case 'plan3': return [anchorYear, anchorYear + 1, anchorYear + 2]
  }
}

export function ExportDialog({
  open, onOpenChange, companyKey, anchorYear,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  companyKey: string
  anchorYear: number
}) {
  const [format, setFormat] = useState<ExportFormat>('excel')
  const [layout, setLayout] = useState<ExportLayout>('standard')
  const [preset, setPreset] = useState<Preset>('current')
  const [from, setFrom] = useState(anchorYear)
  const [to, setTo] = useState(anchorYear + 1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const years = useMemo(
    () => resolveYears(preset, anchorYear, from, to),
    [preset, anchorYear, from, to],
  )

  async function handleExport() {
    setBusy(true)
    setError(null)
    try {
      const datasets: PlanComparisonResponse[] = []
      for (const year of years) {
        const active = await apiClient.get<{ planId: number }>(
          `/liquidity-planning/plans/active?company=${encodeURIComponent(companyKey)}&year=${year}`,
        )
        const cmp = await apiClient.get<PlanComparisonResponse>(
          `/liquidity-planning/plans/${active.data.planId}/comparison`,
        )
        datasets.push(cmp.data)
      }
      await exportPlans(datasets, { format, layout })
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Export"
      description="Zeitraum, Format und Layout wählen."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button variant="primary" onClick={handleExport} disabled={busy || years.length === 0}>
            <Download className="size-4 mr-1" />
            {busy ? 'Exportiere…' : `Exportieren (${years.length} ${years.length === 1 ? 'Jahr' : 'Jahre'})`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Zeitraum</label>
          <SelectRoot value={preset} onValueChange={(v) => setPreset(v as Preset)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESET_ORDER.map((p) => (
                <SelectItem key={p} value={p}>{PRESET_LABELS[p]}</SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          {preset === 'custom' && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Input label="Von Jahr" type="number" value={from} onChange={(e) => setFrom(Number(e.target.value))} />
              <Input label="Bis Jahr" type="number" value={to} onChange={(e) => setTo(Number(e.target.value))} />
            </div>
          )}
          <p className="text-caption text-gray-500 mt-1">
            Jahre: {years.join(', ')}
          </p>
        </div>

        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Format</label>
          <div className="flex gap-2">
            <Button variant={format === 'excel' ? 'primary' : 'secondary'} size="sm" onClick={() => setFormat('excel')}>
              <FileSpreadsheet className="size-4 mr-1" /> Excel (.xlsx)
            </Button>
            <Button variant={format === 'pdf' ? 'primary' : 'secondary'} size="sm" onClick={() => setFormat('pdf')}>
              <FileText className="size-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Layout</label>
          <SelectRoot value={layout} onValueChange={(v) => setLayout(v as ExportLayout)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard (PLAN/IST untereinander)</SelectItem>
              <SelectItem value="banken">Banken (PLAN/IST nebeneinander, Abweichung darunter)</SelectItem>
            </SelectContent>
          </SelectRoot>
          {format === 'pdf' && (
            <p className="text-caption text-gray-500 mt-1">
              PDF öffnet den Druck-Dialog — dort „Als PDF speichern" wählen.
            </p>
          )}
        </div>

        {error && <p className="text-small text-red-600">{error}</p>}
      </div>
    </Modal>
  )
}
